import { useState } from 'react';
import type { BBSUser, BBSPost, BBSAttachment } from '../types';
import { CATEGORIES } from '../types';
import { useLang } from '../../../hooks/useLang';

interface CreatePostModalProps {
  open: boolean;
  onClose: () => void;
  currentUser: BBSUser;
  onSubmit: (post: Omit<BBSPost, 'id' | 'likes' | 'views' | 'replies' | 'pinned' | 'createdAt' | 'updatedAt'>) => void;
}

const ALLOWED_TYPES = ['.pdf', '.doc', '.docx', '.xlsx', '.xls', '.ppt', '.pptx'];

const CREATE_POST_LABELS: Record<string, Record<string, string>> = {
  title: { zh: '发布新帖子', en: 'Create New Post', fr: 'Nouveau Post', es: 'Nueva Publicación', pt: 'Nova Postagem', ja: '新規投稿', de: 'Neuer Beitrag', ko: '새 글 작성', ar: 'منشور جديد', ru: 'Новый Пост' },
  postingAs: { zh: '以', en: 'Posting as', fr: 'Publier en tant que', es: 'Publicando como', pt: 'Postando como', ja: 'として投稿', de: 'Veröffentlichen als', ko: '로 작성 중', ar: 'النشر كـ', ru: 'Публикует' },
  selectCategory: { zh: '选择板块', en: 'Select Category', fr: 'Choisir la catégorie', es: 'Seleccionar categoría', pt: 'Selecionar categoria', ja: 'カテゴリを選択', de: 'Kategorie wählen', ko: '카테고리 선택', ar: 'اختيار الفئة', ru: 'Выбрать категорию' },
  postTitle: { zh: '标题', en: 'Title', fr: 'Titre', es: 'Título', pt: 'Título', ja: 'タイトル', de: 'Titel', ko: '제목', ar: 'العنوان', ru: 'Заголовок' },
  titlePlaceholder: { zh: '请输入帖子标题（5-100字）', en: 'Enter post title (5-100 chars)', fr: 'Entrez le titre (5-100 caractères)', es: 'Ingrese el título (5-100 caracteres)', pt: 'Digite o título (5-100 caracteres)', ja: 'タイトルを入力（5-100文字）', de: 'Titel eingeben (5-100 Zeichen)', ko: '제목 입력 (5-100자)', ar: 'أدخل العنوان (5-100 حرف)', ru: 'Введите заголовок (5-100 символов)' },
  content: { zh: '内容', en: 'Content', fr: 'Contenu', es: 'Contenido', pt: 'Conteúdo', ja: '内容', de: 'Inhalt', ko: '내용', ar: 'المحتوى', ru: 'Содержание' },
  contentPlaceholder: { zh: '请输入帖子内容...\n\n支持基本格式：**粗体**、# 标题 等', en: 'Enter post content...\n\nSupports basic formatting: **bold**, # heading, etc.', fr: 'Entrez le contenu...\n\nFormats supportés: **gras**, # titre, etc.', es: 'Ingrese el contenido...\n\nSoporta formato básico: **negrita**, # título, etc.', pt: 'Digite o conteúdo...\n\nSuporta formatação básica: **negrito**, # título, etc.', ja: '内容を入力...\n\n基本フォーマット対応：**太字**、# 見出し など', de: 'Inhalt eingeben...\n\nUnterstützt grundlegende Formatierung: **fett**, # Überschrift usw.', ko: '내용을 입력...\n\n기본 서식 지원: **굵게**, # 제목 등', ar: 'أدخل المحتوى...\n\nيدعم التنسيق الأساسي: **عريض**، # عنوان، إلخ', ru: 'Введите содержание...\n\nПоддерживает базовое форматирование: **жирный**, # заголовок и т.д.' },
  tags: { zh: '标签（逗号分隔，选填）', en: 'Tags (comma separated, optional)', fr: 'Tags (séparés par virgule, optionnel)', es: 'Etiquetas (separadas por coma, opcional)', pt: 'Tags (separadas por vírgula, opcional)', ja: 'タグ（カンマ区切り、任意）', de: 'Tags (kommagetrennt, optional)', ko: '태그 (쉼표로 구분, 선택)', ar: 'الوسوم (مفصولة بفاصلة، اختياري)', ru: 'Теги (через запятую, опционально)' },
  tagsPlaceholder: { zh: '如：SFC, RWA, 监管', en: 'e.g.: SFC, RWA, Regulation', fr: 'ex: SFC, RWA, Régulation', es: 'ej.: SFC, RWA, Regulación', pt: 'ex: SFC, RWA, Regulação', ja: '例：SFC、RWA、規制', de: 'z.B.: SFC, RWA, Regulierung', ko: '예: SFC, RWA, 규제', ar: 'مثال: SFC، RWA، تنظيم', ru: 'напр.: SFC, RWA, Регулирование' },
  attachments: { zh: '附件上传（选填）', en: 'Attachments (optional)', fr: 'Pièces jointes (optionnel)', es: 'Adjuntos (opcional)', pt: 'Anexos (opcional)', ja: '添付ファイル（任意）', de: 'Anhänge (optional)', ko: '첨부파일 (선택)', ar: 'المرفقات (اختياري)', ru: 'Вложения (опционально)' },
  attachmentsHint: { zh: '支持 PDF / Word / Excel / PPT，单文件≤50MB', en: 'Supports PDF / Word / Excel / PPT, max 50MB per file', fr: 'PDF / Word / Excel / PPT, max 50Mo par fichier', es: 'PDF / Word / Excel / PPT, máx 50MB por archivo', pt: 'PDF / Word / Excel / PPT, máx 50MB por arquivo', ja: 'PDF / Word / Excel / PPT 対応、ファイルあたり最大50MB', de: 'PDF / Word / Excel / PPT, max. 50MB pro Datei', ko: 'PDF / Word / Excel / PPT 지원, 파일당 최대 50MB', ar: 'يدعم PDF / Word / Excel / PPT، بحد أقصى 50 ميجابايت لكل ملف', ru: 'PDF / Word / Excel / PPT, макс. 50МБ на файл' },
  uploadFile: { zh: '点击上传文件', en: 'Click to upload file', fr: 'Cliquez pour télécharger', es: 'Haga clic para subir', pt: 'Clique para enviar', ja: 'クリックしてアップロード', de: 'Klicken zum Hochladen', ko: '클릭하여 업로드', ar: 'انقر للرفع', ru: 'Нажмите для загрузки' },
  complianceNotice: { zh: '发帖前请确认内容合规：不涉及投资建议、不违反中国内地及香港法律法规、不包含受版权保护的商业资料。', en: 'Please ensure compliance: no investment advice, no violations of Mainland China/HK laws, no copyrighted commercial materials.', fr: 'Veuillez confirmer la conformité: pas de conseils d\'investissement, respect des lois chinoises/hongkongaises, pas de contenu protégé.', es: 'Asegúrese de cumplir: sin asesoramiento de inversión, sin violar leyes de China continental/HK, sin materiales protegidos por derechos de autor.', pt: 'Confirme a conformidade: sem aconselhamento de investimento, sem violar leis da China continental/HK, sem materiais protegidos.', ja: 'コンプライアンスを確認：投資助言なし、中国本土・香港の法律違反なし、著作権保護資料なし。', de: 'Bitte stellen Sie die Einhaltung sicher: keine Anlageberatung, keine Verstöße gegen chinesische/HK-Gesetze, keine urheberrechtlich geschützten Materialien.', ko: '컴플라이언스 확인: 투자 조언 없음, 중국 본토/홍콩 법률 위반 없음, 저작권 보호 자료 없음.', ar: 'يرجى التأكد من الامتثال: بدون نصائح استثمارية، بدون انتهاك قوانين الصين القارية/هونغ كونغ، بدون مواد محمية بحقوق الطبع.', ru: 'Убедитесь в соблюдении: без инвестиционных советов, без нарушений законов Китая/Гонконга, без материалов, защищенных авторским правом.' },
  cancel: { zh: '取消', en: 'Cancel', fr: 'Annuler', es: 'Cancelar', pt: 'Cancelar', ja: 'キャンセル', de: 'Abbrechen', ko: '취소', ar: 'إلغاء', ru: 'Отмена' },
  submit: { zh: '发布帖子', en: 'Publish Post', fr: 'Publier', es: 'Publicar', pt: 'Publicar', ja: '投稿する', de: 'Veröffentlichen', ko: '게시하기', ar: 'نشر', ru: 'Опубликовать' },
  errorTitle: { zh: '请填写帖子标题', en: 'Please enter post title', fr: 'Veuillez entrer le titre', es: 'Ingrese el título', pt: 'Digite o título', ja: 'タイトルを入力してください', de: 'Bitte Titel eingeben', ko: '제목을 입력하세요', ar: 'الرجاء إدخال العنوان', ru: 'Введите заголовок' },
  errorContent: { zh: '请填写帖子内容', en: 'Please enter post content', fr: 'Veuillez entrer le contenu', es: 'Ingrese el contenido', pt: 'Digite o conteúdo', ja: '内容を入力してください', de: 'Bitte Inhalt eingeben', ko: '내용을 입력하세요', ar: 'الرجاء إدخال المحتوى', ru: 'Введите содержание' },
};

export default function CreatePostModal({ open, onClose, currentUser, onSubmit }: CreatePostModalProps) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('rwa-discuss');
  const [tags, setTags] = useState('');
  const [attachments, setAttachments] = useState<BBSAttachment[]>([]);
  const [error, setError] = useState('');
  const { lang } = useLang();
  const getLabel = (key: string) => CREATE_POST_LABELS[key]?.[lang] ?? CREATE_POST_LABELS[key]?.['en'] ?? key;

  if (!open) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const newAtts: BBSAttachment[] = files.map((f) => ({
      id: `att_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      name: f.name,
      size: f.size > 1024 * 1024 ? `${(f.size / (1024 * 1024)).toFixed(1)} MB` : `${(f.size / 1024).toFixed(0)} KB`,
      type: f.name.split('.').pop()?.toLowerCase() || 'file',
      uploadedAt: new Date().toISOString().slice(0, 10),
    }));
    setAttachments((prev) => [...prev, ...newAtts]);
  };

  const handleSubmit = () => {
    setError('');
    if (!title.trim()) { setError(getLabel('errorTitle')); return; }
    if (!content.trim()) { setError(getLabel('errorContent')); return; }
    onSubmit({
      title: title.trim(),
      content: content.trim(),
      authorId: currentUser.id,
      category,
      tags: tags.split(/[,，\s]+/).filter(Boolean),
      attachments,
    });
    setTitle(''); setContent(''); setTags(''); setAttachments([]);
    onClose();
  };

  const catOptions = CATEGORIES.filter((c) => c.id !== 'all');

  return (
    <div className="fixed inset-0 z-[99997] flex items-center justify-center p-4 overflow-y-auto">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="font-black text-gray-900">{getLabel('title')}</h2>
            <p className="text-gray-400 text-xs mt-0.5">{getLabel('postingAs')} <span className="text-[#7C3AED] font-semibold">{currentUser.username}</span></p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-700 cursor-pointer transition-colors">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              <i className="ri-error-warning-line" />{error}
            </div>
          )}

          {/* Category */}
          <div>
            <label className="text-xs font-bold text-gray-600 block mb-2">{getLabel('selectCategory')}</label>
            <div className="flex flex-wrap gap-2">
              {catOptions.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setCategory(cat.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-all cursor-pointer whitespace-nowrap ${
                    category === cat.id
                      ? 'text-white border-transparent'
                      : 'text-gray-500 border-gray-200 hover:border-gray-300'
                  }`}
                  style={category === cat.id ? { background: cat.color, borderColor: cat.color } : {}}
                >
                  <i className={`${cat.icon} text-xs`} />
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="text-xs font-bold text-gray-600 block mb-1.5">{getLabel('postTitle')} *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={getLabel('titlePlaceholder')}
              maxLength={100}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#7C3AED] transition-colors"
            />
          </div>

          {/* Content */}
          <div>
            <label className="text-xs font-bold text-gray-600 block mb-1.5">{getLabel('content')} *</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={getLabel('contentPlaceholder')}
              rows={8}
              maxLength={5000}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#7C3AED] resize-none transition-colors"
            />
            <div className="text-right text-xs text-gray-400 mt-1">{content.length}/5000</div>
          </div>

          {/* Tags */}
          <div>
            <label className="text-xs font-bold text-gray-600 block mb-1.5">{getLabel('tags')}</label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder={getLabel('tagsPlaceholder')}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#7C3AED] transition-colors"
            />
          </div>

          {/* File Upload */}
          <div>
            <label className="text-xs font-bold text-gray-600 block mb-1.5">
              {getLabel('attachments')}
              <span className="text-gray-400 font-normal ml-1">{getLabel('attachmentsHint')}</span>
            </label>
            <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-[#7C3AED]/50 hover:bg-[#7C3AED]/5 transition-all group">
              <div className="flex items-center gap-2 text-gray-400 group-hover:text-[#7C3AED] transition-colors">
                <i className="ri-upload-cloud-2-line text-2xl" />
                <div className="text-center">
                  <div className="text-sm font-semibold">{getLabel('uploadFile')}</div>
                  <div className="text-xs">{ALLOWED_TYPES.join(' / ')}</div>
                </div>
              </div>
              <input
                type="file"
                multiple
                accept={ALLOWED_TYPES.join(',')}
                onChange={handleFileChange}
                className="hidden"
              />
            </label>
            {attachments.length > 0 && (
              <div className="mt-2 space-y-1.5">
                {attachments.map((att) => (
                  <div key={att.id} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
                    <i className="ri-file-line text-[#7C3AED] text-sm" />
                    <span className="text-xs text-gray-700 flex-1 truncate">{att.name}</span>
                    <span className="text-xs text-gray-400">{att.size}</span>
                    <button
                      onClick={() => setAttachments((prev) => prev.filter((a) => a.id !== att.id))}
                      className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-red-400 cursor-pointer transition-colors"
                    >
                      <i className="ri-close-line text-xs" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Compliance Notice */}
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl">
            <i className="ri-shield-check-line text-amber-600 mt-0.5 shrink-0" />
            <p className="text-amber-700 text-xs leading-relaxed">
              {getLabel('complianceNotice')}
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              className="flex-1 py-3 border border-gray-200 text-gray-600 text-sm font-bold rounded-xl hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              {getLabel('cancel')}
            </button>
            <button
              onClick={handleSubmit}
              className="flex-2 flex-grow-[2] py-3 bg-gradient-to-r from-[#7C3AED] to-[#A855F7] text-white text-sm font-black rounded-xl hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
            >
              {getLabel('submit')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
